import { createStore } from 'vuex'
import axios from 'axios'
import { BASE_URL } from '@/config';
import MessageUtil from '@/utils/MessageUtil';
import { analyzeDuplicates } from '@/utils/segmentDuplicates';
import { saveDraft as savePoolDraft, clearDraft as clearPoolDraft } from '@/utils/poolDraftStorage';

// 片段文本截断（用于 toast / 提示文案）
function truncateText(text, maxLen) {
  if (!text) return '片段'
  const t = String(text).replace(/\s+/g, '')
  return t.length > maxLen ? t.slice(0, maxLen) + '…' : t
}

// 检测新增片段与池内已有片段是否重复/重叠，若命中则弹全局 toast 提醒
// （放在 store action 里执行，剪辑池面板即使隐藏/不在视窗内也能正常提醒）
function notifyDuplicateToast(commit, state, addedSegmentIds) {
  if (!addedSegmentIds || addedSegmentIds.length === 0) return

  const allSegments = []
  state.groups.forEach(g => {
    if (g && g.segments) g.segments.forEach(s => allSegments.push(s))
  })

  const { dupMap, segById } = analyzeDuplicates(allSegments)
  const hits = addedSegmentIds.filter(id => dupMap[id])
  if (hits.length === 0) return

  const parts = []
  hits.slice(0, 3).forEach(id => {
    const seg = segById[id]
    const info = dupMap[id]
    if (!seg || !info) return
    const segText = truncateText(seg.text || seg.video_name, 16)
    if (info.type === 'dup') {
      const pairTexts = info.pairIds.slice(0, 2).map(pid => {
        const p = segById[pid]
        return p ? `「${truncateText(p.text || p.video_name, 10)}」` : ''
      }).filter(Boolean).join('、')
      parts.push(`片段「${segText}」与${pairTexts || '池中片段'}完全重复`)
    } else {
      parts.push(`片段「${segText}」与池中 ${info.pairIds.length} 条片段时间重叠`)
    }
  })

  commit('snackbar/show', {
    text: '⚠ 检测到重复素材：' + parts.join('；'),
    color: 'warning',
    timeout: 6000
  }, { root: true })
}

// 记录池中曾出现过的片段 id，用于区分「真正新增素材」与「组间移动/重排」，
// 避免把拖拽移动误判为重复素材而误弹 toast
const seenSegmentIds = new Set()
function syncSeenIds(state) {
  state.groups.forEach(g => {
    if (g && g.segments) g.segments.forEach(s => {
      if (s && s.id) seenSegmentIds.add(s.id)
    })
  })
}

// 草稿自动保存相关状态（模块级，供 action 与持久化插件共享）
let draftSaveTimer = null
let suppressDraft = false
const DRAFT_SAVE_DEBOUNCE = 800

const authModule = {
  state: {
    user: null
  },
  mutations: {    
    setUser(state, value) {
      state.user = value
    }
  },
  actions: {
    async login({ commit }, { phoneNumber, password }) {
      return new Promise((resolve, reject) => {
        axios.post(BASE_URL+'auth/token', {
          phoneNumber: phoneNumber,
          password: password
          })
          .then(response => {
            const token = response.access_token
            localStorage.setItem('token', token)
            localStorage.setItem('user',JSON.stringify(response.user))
            commit('setUser', response.user)
            resolve(response)
          })
          .catch(error => {
            if (error && error.code === 403001) {
              // Non-VIP user logic handled by component
            } else {
              MessageUtil.handleApiError(error, '登录失败');
            }
            reject(error)
          }
        )
      })
    },
    logout({ commit }) {
      // Here you might call a service that logs out your user.
      // For the sake of this example, we will just commit the mutations.
      commit('setUser', null);
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    }
  },
  getters: {
    
    currentUser(state) {
      return state.user;
    }
  }
};

const multiClipperModule = {
  namespaced: true,
  state: {
    groups: [], // { id, name, segments: [] }
    currentGroupIndex: -1,
    previewSource: null, // { video_path, ... }
    activePanel: 'source', // 'source' | 'pool'
    sourceTraceSegments: [], // Hot video source trace segments
    aiReferenceGroups: [], // Groups that were disassembled and became reference
    disassemblyResults: {}, // { groupId: { category: [ {text, start_time, end_time, video_id} ] } }
    externalSelectionRange: null, // { start_time, end_time, video_id }
    highlightRanges: [] // Array of { start_time, end_time, video_id }
  },
  mutations: {
    SET_DISASSEMBLY_RESULT(state, { groupId, result }) {
      state.disassemblyResults = {
        ...state.disassemblyResults,
        [groupId]: result
      }
    },
    SET_HIGHLIGHT_RANGES(state, ranges) {
      state.highlightRanges = ranges
    },
    SET_SOURCE_TRACE_SEGMENTS(state, segments) {
      state.sourceTraceSegments = segments
    },
    ADD_AI_REFERENCE_GROUP(state, group) {
      state.aiReferenceGroups.unshift(group)
    },
    REMOVE_AI_REFERENCE_GROUP(state, index) {
      state.aiReferenceGroups.splice(index, 1)
    },
    SET_EXTERNAL_SELECTION_RANGE(state, range) {
      state.externalSelectionRange = range
    },
    SET_ACTIVE_PANEL(state, panel) {
      state.activePanel = panel
    },
    ADD_GROUP(state, group) {
      state.groups.push(group)
      if (state.currentGroupIndex === -1) {
        state.currentGroupIndex = 0
      }
    },
    ADD_SEGMENT(state, { groupIndex, segment }) {
      if (state.groups[groupIndex]) {
        // Create new array reference to ensure reactivity in child components
        state.groups[groupIndex].segments = [...state.groups[groupIndex].segments, segment]
      }
    },
    SET_PREVIEW_SOURCE(state, source) {
      if (state.previewSource && source) {
        const oldId = state.previewSource.transcription_id || state.previewSource.video_id || state.previewSource.id;
        const newId = source.transcription_id || source.video_id || source.id;
        if (oldId && newId && oldId === newId) {
          source = {
            ...state.previewSource,
            ...source
          };
        }
      }
      state.previewSource = source
    },
    SET_CURRENT_GROUP_INDEX(state, index) {
      state.currentGroupIndex = index
    },
    DELETE_SEGMENT(state, { groupIndex, segmentIndex }) {
      if (state.groups[groupIndex]) {
        // Create new array reference
        const newSegments = [...state.groups[groupIndex].segments]
        newSegments.splice(segmentIndex, 1)
        state.groups[groupIndex].segments = newSegments
      }
    },
    DELETE_GROUP(state, index) {
      state.groups.splice(index, 1)
      if (state.groups.length === 0) {
        state.currentGroupIndex = -1
      } else if (state.currentGroupIndex >= state.groups.length) {
        state.currentGroupIndex = state.groups.length - 1
      }
    },
    UPDATE_GROUP_SEGMENTS(state, { groupIndex, segments }) {
      if (state.groups[groupIndex]) {
        state.groups[groupIndex].segments = segments
      }
    },
    UPDATE_GROUP_NAME(state, { groupIndex, name }) {
      if (state.groups[groupIndex]) {
        state.groups[groupIndex].name = name
      }
    },
    SET_GROUPS(state, groups) {
      state.groups = groups
    },
    UPDATE_SEGMENT(state, { groupIndex, segmentIndex, segment }) {
      if (state.groups[groupIndex] && state.groups[groupIndex].segments[segmentIndex]) {
        // Create new array reference
        const newSegments = [...state.groups[groupIndex].segments]
        newSegments.splice(segmentIndex, 1, segment)
        state.groups[groupIndex].segments = newSegments
      }
    }
  },
  actions: {
    createGroup({ commit, state }, name) {
      const nextId = state.groups.length + 1;
      commit('ADD_GROUP', {
        id: Date.now().toString(),
        name: name || `组${nextId}`,
        segments: []
      })
      commit('SET_CURRENT_GROUP_INDEX', state.groups.length - 1)
    },
    updateGroupName({ commit }, payload) {
      commit('UPDATE_GROUP_NAME', payload)
    },
    updateGroupsOrder({ commit, state }, groups) {
      commit('SET_GROUPS', groups)
      syncSeenIds(state)
    },
    updateSegment({ commit }, payload) {
      commit('UPDATE_SEGMENT', payload)
    },
    addToGroup({ commit, state }, segment) {
      if (state.currentGroupIndex !== -1) {
        commit('ADD_SEGMENT', {
          groupIndex: state.currentGroupIndex,
          segment
        })
      } else {
        // Create default group if none exists
        const nextId = state.groups.length + 1;
        commit('ADD_GROUP', {
          id: Date.now().toString(),
          name: `组${nextId}`,
          segments: [segment]
        })
        commit('SET_CURRENT_GROUP_INDEX', state.groups.length - 1)
      }
      syncSeenIds(state)
      // 新素材入池后检测是否与池内已有片段重复/重叠，弹全局 toast 提醒
      if (segment && segment.id) {
        notifyDuplicateToast(commit, state, [segment.id])
      }
    },
    deleteSegment({ commit }, payload) {
      commit('DELETE_SEGMENT', payload)
    },
    deleteGroup({ commit }, index) {
      commit('DELETE_GROUP', index)
    },
    // 清空整个剪辑池：移除所有组并重置当前选中组
    clearAllGroups({ commit }) {
      commit('SET_GROUPS', [])
      commit('SET_CURRENT_GROUP_INDEX', -1)
    },
    selectPreview({ commit }, source) {
      commit('SET_PREVIEW_SOURCE', source)
    },
    updateGroupSegments({ commit, state }, payload) {
      const { groupIndex, segments, silent } = payload
      // 对比新旧片段，找出本次「真正新增」的片段 id：
      // 仅当该 id 既不在本组旧列表中、也从未在池中出现过（seenSegmentIds）时，才视为新素材
      const oldSegs = (state.groups[groupIndex] && state.groups[groupIndex].segments) || []
      const oldIds = new Set(oldSegs.map(s => s && s.id))
      const addedIds = (segments || [])
        .filter(s => s && s.id && !oldIds.has(s.id) && !seenSegmentIds.has(s.id))
        .map(s => s.id)

      commit('UPDATE_GROUP_SEGMENTS', payload)
      syncSeenIds(state)

      // 批量操作（AI 编排/拆分/合并/移动等）标记 silent 跳过；仅手动添加/拖拽单条时提醒
      if (!silent && addedIds.length > 0 && addedIds.length <= 3) {
        notifyDuplicateToast(commit, state, addedIds)
      }
    },
    addAiReferenceGroup({ commit }, group) {
      commit('ADD_AI_REFERENCE_GROUP', group)
    },
    removeAiReferenceGroup({ commit }, index) {
      commit('REMOVE_AI_REFERENCE_GROUP', index)
    },
    setDisassemblyResult({ commit }, payload) {
      commit('SET_DISASSEMBLY_RESULT', payload)
    },
    setActivePanel({ commit }, panel) {
      commit('SET_ACTIVE_PANEL', panel)
    },
    setSourceTraceSegments({ commit }, segments) {
            commit('SET_SOURCE_TRACE_SEGMENTS', segments)
          },
          setExternalSelectionRange({ commit }, range) {
      commit('SET_EXTERNAL_SELECTION_RANGE', range)
    },
    setHighlightRanges({ commit }, ranges) {
      commit('SET_HIGHLIGHT_RANGES', ranges)
    },
    // 清空当前标签页的草稿（导出全部成功后调用）。
    // 同时挂起自动保存，避免页面刷新时 beforeunload 兜底又把草稿写回。
    clearDraft() {
      suppressDraft = true
      clearTimeout(draftSaveTimer)
      draftSaveTimer = null
      clearPoolDraft()
    },
    // 恢复指定草稿到剪辑池
    restoreDraft({ commit, state }, draft) {
      if (!draft) return
      const groups = draft.groups || []
      commit('SET_GROUPS', groups)
      let idx = -1
      if (groups.length > 0) {
        const saved = Number(draft.currentGroupIndex)
        idx = Number.isFinite(saved) && saved >= 0 && saved < groups.length ? saved : 0
      }
      commit('SET_CURRENT_GROUP_INDEX', idx)
      // 同步已见片段 id，避免恢复后把池内已有素材误判为「新素材」弹重复提示
      syncSeenIds(state)
    },
  },
};

// 需要触发草稿保存的剪辑内容变更（浏览态变更如切面板/预览不参与保存）
const DRAFT_MUTATIONS = new Set([
  'ADD_GROUP', 'ADD_SEGMENT', 'DELETE_SEGMENT', 'DELETE_GROUP',
  'UPDATE_GROUP_SEGMENTS', 'UPDATE_GROUP_NAME', 'SET_GROUPS', 'UPDATE_SEGMENT',
  'SET_CURRENT_GROUP_INDEX'
])

// 剪辑池草稿自动保存：监听剪辑内容相关 mutation，防抖后写入 IndexedDB。
// 池为空时不保留草稿；页面关闭/刷新前兜底刷一次，减少防抖窗口内的丢失。
function persistPoolDraft(state) {
  const pool = state.multiClipper
  if (!pool) return
  const groups = pool.groups || []
  if (groups.length === 0) {
    clearPoolDraft()
  } else {
    savePoolDraft({
      groups,
      currentGroupIndex: pool.currentGroupIndex
    })
  }
}

function draftPersistencePlugin(store) {
  store.subscribe((mutation, state) => {
    if (!mutation.type.startsWith('multiClipper/')) return
    const type = mutation.type.slice('multiClipper/'.length)
    if (!DRAFT_MUTATIONS.has(type)) return
    // 用户又编辑了剪辑内容，恢复自动保存
    suppressDraft = false
    clearTimeout(draftSaveTimer)
    draftSaveTimer = setTimeout(() => persistPoolDraft(state), DRAFT_SAVE_DEBOUNCE)
  })
  window.addEventListener('beforeunload', () => {
    clearTimeout(draftSaveTimer)
    // 导出成功后已挂起自动保存，刷新时不再把已导出的内容写回草稿
    if (!suppressDraft) persistPoolDraft(store.state)
  })
}

export default createStore({
  plugins: [draftPersistencePlugin],
  state: {
    
    currentVideo: null,
    currentTextSpan: []
  },
  mutations: {

    setCurrentVideo(state, value) {
      state.currentVideo = value
    },
    setCurrentTextSpan(state, value) {
      state.currentTextSpan = value
    }
  },

  modules: {
    multiClipper: multiClipperModule,
    auth: authModule,
    snackbar: {
      namespaced: true,
      state: {
        visible: false,
        text: '',
        color: 'error',
        timeout: 3000
      },
      mutations: {
        show(state, payload) {
          state.text = payload.text;
          state.color = payload.color || 'error';
          state.timeout = payload.timeout || 3000;
          state.visible = true;
        },
        close(state) {
          state.visible = false;
        }
      }
    }
  }
})
